// RFNode.js - Custom Node Component with Compression Visualization

import React, { memo, useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import styled from 'styled-components';
import useStore from '../../store/useStore';
import api from '../../utils/api';
import THEME from '../../styles/theme';
import { COMPONENT_LIBRARY } from '../../data/componentLibrary';
import VoltageSelector from '../VoltageSelector';
import DataSourceBadge from '../DataSourceBadge';
import SmithChartWindow from '../SmithChartWindow';
import SmithChartModal from '../SmithChartModal';
import { getAvailableDataSources, getCurrentDataSource } from '../../utils/dataSourceHelpers';
import { RecommendationBadge } from '../Recommendations';
import { interpolateParameter } from '../../utils/freqDataInterpolator';
import { interpolateS21 } from '../../utils/s2pInterpolator';

// Toggle between old floating window (false) and new modal dialog (true)
const USE_MODAL_SMITH_CHART = true;

// RF Component Symbol Watermarks (SVG paths)
const COMPONENT_SYMBOLS = {
  // Sources: Sine wave
  inputSource: 'M8,20 Q12,10 16,20 T24,20 T32,20',
  noiseSource: 'M8,20 Q12,10 16,20 T24,20 T32,20',

  // Amplifiers: Triangle
  lna: 'M10,10 L30,20 L10,30 Z',
  powerAmplifier: 'M10,10 L30,20 L10,30 Z',
  vga: 'M10,10 L30,20 L10,30 Z',
  gainBlock: 'M10,10 L30,20 L10,30 Z',
  driver: 'M10,10 L30,20 L10,30 Z',

  // Attenuators: Zigzag resistor pattern
  attenuator: 'M10,20 L13,15 L16,25 L19,15 L22,25 L25,15 L28,20 L30,20',
  variableAttenuator: 'M10,20 L13,15 L16,25 L19,15 L22,25 L25,15 L28,20 L30,20 M12,28 L28,12',

  // Cable: Simple horizontal line
  cable: 'M10,20 L30,20',

  // Splitter/Combiner: Y-shape
  splitter: 'M10,20 L20,20 M20,15 L25,12 M20,25 L25,28',
  combiner: 'M30,20 L20,20 M20,15 L15,12 M20,25 L15,28',

  // Filters: Frequency response curves
  bandpassFilter: 'M10,25 L13,25 L16,10 L19,10 L22,25 L25,25',
  lowpassFilter: 'M10,10 L15,10 Q20,15 25,25',
  highpassFilter: 'M10,25 Q15,15 20,10 L25,10',
  bandstopFilter: 'M10,15 L13,15 L16,25 L19,25 L22,15 L25,15',

  // Mixers: X symbol
  passiveMixer: 'M10,10 L30,30 M30,10 L10,30 M15,20 L25,20',
  activeMixer: 'M10,10 L30,30 M30,10 L10,30 M15,20 L25,20',

  // Switches: Contact points
  spdtSwitch: 'M10,20 L18,20 L25,15 M25,25 L24,24',
  sp4tSwitch: 'M10,20 L18,20 M22,12 L21,13 M28,15 L27,16 M28,25 L27,24 M22,28 L21,27',

  // Termination: Ground symbol
  termination: 'M20,15 L20,25 M15,25 L25,25 M16,27 L24,27 M18,29 L22,29',

  // Generic: Circle
  generic: 'M20,20 m-8,0 a8,8 0 1,0 16,0 a8,8 0 1,0 -16,0',

  // Note: Document icon
  note: 'M12,10 L25,10 L28,13 L28,30 L12,30 Z M25,10 L25,13 L28,13'
};

const RFNode = ({ data, id, type }) => {
  const {
    calculationResults,
    componentLibrary,
    updateNodeData,
    handleDataSourceChange,
    nodes,
    // Frequency state and actions
    frequencyMode,
    frequencySingle,
    frequencyStart,
    frequencyStop,
    frequencyStep,
    setFrequencyMode,
    setFrequencySingle,
    setFrequencySweepParams,
    isSweeping,
    // Recommendations
    replaceNodeWithCatalog,
    catalogComponents,
    // Tutorial
    showTutorial,
    tutorialStep,
    completeTutorialAction
  } = useStore();

  // Get setNodes from store for selecting nodes
  const setNodes = useStore(state => state.setNodes);

  // State for tracking which parameter is being edited
  const [editingParam, setEditingParam] = useState(null);
  const [editValue, setEditValue] = useState('');

  // State for editing alias inline
  const [isEditingAlias, setIsEditingAlias] = useState(false);
  const [aliasValue, setAliasValue] = useState('');

  // State for context menu
  const [contextMenu, setContextMenu] = useState(null);

  // State for Smith chart window
  const [showSmithChart, setShowSmithChart] = useState(false);

  // Check if component has S2P data
  const hasS2pData = !!data.parameters?.s2pData?.value;

  // Watch for trigger from context menu to enter edit mode
  React.useEffect(() => {
    if (data._triggerAliasEdit) {
      setIsEditingAlias(true);
      setAliasValue(data.alias || '');
      // Clear the trigger flag
      updateNodeData(id, { _triggerAliasEdit: undefined });
    }
  }, [data._triggerAliasEdit, data.alias, id, updateNodeData]);

  // Get component definition - for catalog components, use stored definition
  let componentDef = componentLibrary?.[type] || COMPONENT_LIBRARY[type];

  // Determine symbol type for catalog components
  let symbolType = type;
  if (data.isCatalog) {
    // First try node data baseType
    if (data.baseType) {
      symbolType = data.baseType;
    }
    // Then try component library baseType
    else if (componentDef?.baseType) {
      symbolType = componentDef.baseType;
    }
    // Fallback: infer from category
    else if (componentDef?.category) {
      const categoryMap = {
        'amplifiers': 'powerAmplifier',
        'filters': 'bandpassFilter',
        'passive': 'attenuator',
        'mixers': 'passiveMixer',
        'switches': 'spdtSwitch',
        'sources': 'inputSource'
      };
      symbolType = categoryMap[componentDef.category] || 'generic';
    }
  }

  const results = calculationResults[id];

  // Track compression for tutorial - only on compression step (index 12)
  React.useEffect(() => {
    if (showTutorial && tutorialStep === 12 && results?.compressionState &&
        (results.compressionState === 'warning' || results.compressionState === 'compressed' || results.compressionState === 'saturated')) {
      completeTutorialAction('compressionTriggered');
    }
  }, [showTutorial, tutorialStep, results?.compressionState, completeTutorialAction]);

  // Determine header color based on component type only (not compression state)
  let headerColor = THEME.headerNormal;
  if (type === 'inputSource' || type === 'noiseSource') {
    headerColor = '#1e3a8a'; // Deep blue like Measurement node
  }

  // Get parameters to display on card
  // Hide frequency parameter on inputSource - we now have custom frequency controls
  const displayParams = Object.entries(data.parameters || {})
    .filter(([key, param]) => param.displayOnCard)
    .filter(([key]) => !(type === 'inputSource' && key === 'frequency'));

  const hasInputs = componentDef?.inputs && componentDef.inputs.length > 0;
  const hasOutputs = componentDef?.outputs && componentDef.outputs.length > 0;

  // Calculate minimum height based on number of handles
  const maxHandles = Math.max(
    componentDef?.inputs?.length || 0,
    componentDef?.outputs?.length || 0
  );
  const minBodyHeight = Math.max(100, 60 + maxHandles * 30);

  // Handler functions for hover-reveal editing
  const handleParamClick = (paramKey, currentValue) => {
    setEditingParam(paramKey);
    setEditValue(currentValue);
  };

  const handleEditChange = (e) => {
    setEditValue(e.target.value);
  };

  const handleEditSave = (paramKey) => {
    if (editValue === '') {
      // Cancel if empty
      setEditingParam(null);
      return;
    }

    const param = data.parameters[paramKey];
    const numValue = parseFloat(editValue);

    // Validate number
    if (isNaN(numValue)) {
      console.warn('Invalid number');
      setEditingParam(null);
      return;
    }

    // Validate min/max constraints
    if (param.min !== undefined && numValue < param.min) {
      console.warn(`Value ${numValue} is below minimum ${param.min}`);
      setEditingParam(null);
      return;
    }
    if (param.max !== undefined && numValue > param.max) {
      console.warn(`Value ${numValue} is above maximum ${param.max}`);
      setEditingParam(null);
      return;
    }

    // Check if value actually changed
    const valueChanged = param.value !== numValue;

    // Save to store
    const updatedParams = {
      ...data.parameters,
      [paramKey]: {
        ...param,
        value: numValue
      }
    };

    updateNodeData(id, { parameters: updatedParams });
    setEditingParam(null);

    // Track for tutorial - only on edit-params step (index 13) and only if value actually changed
    if (showTutorial && tutorialStep === 13 && valueChanged) {
      completeTutorialAction('paramEdited');
    }
  };

  const handleEditKeyDown = (event, paramKey) => {
    if (event.key === 'Enter') {
      handleEditSave(paramKey);
    } else if (event.key === 'Escape') {
      setEditingParam(null);
    }
  };

  const handleWheelAdjust = (event, paramKey) => {
    // Prevent canvas zoom
    event.preventDefault();
    event.stopPropagation();

    // Map custom frequency keys to actual parameter keys
    const actualParamKey = paramKey === '_frequency' ? 'frequency' : paramKey;
    const param = data.parameters[actualParamKey];

    // Guard against undefined parameter
    if (!param) return;

    const currentValue = parseFloat(editValue);

    // Determine scroll direction and increment (frequency uses 1 MHz, others use 0.1)
    const baseIncrement = actualParamKey === 'frequency' ? 1 : 0.1;
    const increment = event.deltaY < 0 ? baseIncrement : -baseIncrement;
    const newValue = currentValue + increment;

    // Apply min/max validation
    let clampedValue = newValue;
    if (param.min !== undefined && newValue < param.min) {
      clampedValue = param.min;
    }
    if (param.max !== undefined && newValue > param.max) {
      clampedValue = param.max;
    }

    // Round appropriately to avoid floating point errors
    if (actualParamKey === 'frequency') {
      clampedValue = Math.round(clampedValue);
    } else {
      clampedValue = Math.round(clampedValue * 10) / 10;
    }

    // Update local state for real-time feedback
    setEditValue(clampedValue.toString());

    // Update store immediately for real-time calculations
    const updatedParams = {
      ...data.parameters,
      [actualParamKey]: {
        ...param,
        value: clampedValue
      }
    };

    updateNodeData(id, { parameters: updatedParams });

    // Also update local frequency state if this is the frequency parameter
    if (paramKey === '_frequency') {
      setFrequencySingle(clampedValue);
    }
  };

  // Alias editing handlers
  const handleAliasDoubleClick = (e) => {
    e.stopPropagation(); // Prevent node double-click
    setIsEditingAlias(true);
    setAliasValue(data.alias || '');
  };

  const handleAliasChange = (e) => {
    setAliasValue(e.target.value);
  };

  const handleAliasSave = () => {
    const trimmedAlias = aliasValue.trim();
    updateNodeData(id, { alias: trimmedAlias });
    setIsEditingAlias(false);
  };

  const handleAliasKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleAliasSave();
    } else if (event.key === 'Escape') {
      setIsEditingAlias(false);
      setAliasValue('');
    }
  };

  const handleAliasBlur = () => {
    // Save on blur (clicking away)
    handleAliasSave();
  };

  // Helper to get display value based on data source
  const getDisplayValue = (paramKey, param) => {
    const currentSource = getCurrentDataSource({ id, data }, paramKey);
    const currentFrequency = frequencySingle;

    // Handle S2P data source
    if (currentSource === 's2p' && paramKey === 'gain') {
      const s2pData = data.parameters?.s2pData?.value;
      if (!s2pData || !currentFrequency) {
        return { value: param.value, warning: null };
      }

      const result = interpolateS21(s2pData, currentFrequency);
      return {
        value: result.gain ? parseFloat(result.gain.toFixed(1)) : param.value,
        warning: result.inRange ? null : (result.clampedEdge === 'low' ? 'below' : 'above')
      };
    }

    // Handle database source
    if (currentSource === 'database') {
      const freqData = data.parameters?.freqDbData?.value;

      if (!freqData || !currentFrequency) {
        return { value: param.value, warning: null };
      }

      const paramMap = {
        gain: 'gain_db',
        noiseFigure: 'nf_db',
        p1db: 'p1db_dbm',
        oip3: 'oip3_dbm'
      };

      const dbColumnName = paramMap[paramKey];
      if (!dbColumnName) {
        return { value: param.value, warning: null };
      }

      const result = interpolateParameter(freqData, dbColumnName, currentFrequency);
      // NF uses 2 decimal places, others use 1
      const decimals = paramKey === 'noiseFigure' ? 2 : 1;
      return {
        value: result?.value ? parseFloat(result.value.toFixed(decimals)) : param.value,
        warning: null  // Database doesn't have range warnings yet
      };
    }

    // Manual source - format NF to 2 decimal places
    if (paramKey === 'noiseFigure' && param.value !== null && param.value !== undefined) {
      return { value: parseFloat(Number(param.value).toFixed(2)), warning: null };
    }
    return { value: param.value, warning: null };
  };

  return (
    <NodeContainer
      onWheelCapture={(e) => {
        // If we're editing a parameter, handle wheel anywhere on the node
        if (editingParam !== null) {
          handleWheelAdjust(e, editingParam);
        }
      }}
    >
      {/* Input Handles */}
      {hasInputs && (
        <>
          {componentDef.inputs.map((input, idx) => {
            const numInputs = componentDef.inputs.length;
            const spacing = numInputs > 1 ? 40 : 0;
            const startOffset = 60;
            return (
              <Handle
                key={input}
                type="target"
                position={Position.Left}
                id={input}
                style={{
                  top: `${startOffset + idx * spacing}px`,
                  background: THEME.handleColor,
                  border: `2px solid ${THEME.textPrimary}`
                }}
              />
            );
          })}
        </>
      )}

      {/* Card Content */}
      <Header className="drag-handle" style={{ backgroundColor: headerColor }}>
        {data.isCatalog ? (
          <>
            {isEditingAlias ? (
              <AliasInput
                type="text"
                value={aliasValue}
                onChange={handleAliasChange}
                onKeyDown={handleAliasKeyDown}
                onBlur={handleAliasBlur}
                autoFocus
                placeholder={data.partNumber || data.name}
                $textColor="#ff6b6b"
              />
            ) : (
              <PartNumberText onDoubleClick={handleAliasDoubleClick}>
                {data.alias || data.partNumber || data.name}
              </PartNumberText>
            )}
            {data.alias && !isEditingAlias && (
              <AliasSubtext>
                {data.partNumber}
              </AliasSubtext>
            )}
            <ComponentTypeText style={{ color: headerColor === THEME.headerNormal ? '#000' : '#000' }}>
              {data.baseTypeName || COMPONENT_LIBRARY[symbolType]?.displayName || componentDef?.displayName || 'Catalog Component'}
            </ComponentTypeText>
          </>
        ) : (
          <>
            {isEditingAlias ? (
              <AliasInput
                type="text"
                value={aliasValue}
                onChange={handleAliasChange}
                onKeyDown={handleAliasKeyDown}
                onBlur={handleAliasBlur}
                autoFocus
                placeholder={data.name || componentDef?.displayName || type}
                $textColor={headerColor === THEME.headerNormal ? '#000' : '#fff'}
              />
            ) : (
              <HeaderText
                style={{ color: headerColor === THEME.headerNormal ? '#000' : '#fff' }}
                onDoubleClick={handleAliasDoubleClick}
              >
                {type === 'inputSource' && '📶 '}
                {type === 'noiseSource' && '📢 '}
                {data.alias || data.name || componentDef?.displayName || type}
              </HeaderText>
            )}
            {data.alias && !isEditingAlias && (
              <ComponentTypeText style={{ color: headerColor === THEME.headerNormal ? '#000' : '#000' }}>
                {data.name || componentDef?.displayName || type}
              </ComponentTypeText>
            )}
          </>
        )}
        {hasS2pData && (
          <SmithChartIcon
            id="smith-chart-badge"
            className="nodrag"
            viewBox="0 0 100 100"
            onClick={(e) => {
              e.stopPropagation();
              // Select this node (deselects others)
              setNodes(nds => nds.map(n => ({ ...n, selected: n.id === id })));
              setShowSmithChart(true);
              // Track for tutorial
              if (showTutorial) {
                completeTutorialAction('smithChartClicked');
              }
            }}
            title="View Smith Chart"
          >
            <circle cx="50" cy="50" r="48" />
            <circle cx="74" cy="50" r="24" />
            <circle cx="83" cy="50" r="15" />
            <circle cx="90" cy="50" r="8" />
            <line x1="2" y1="50" x2="98" y2="50" />
            <path d="M 98 50 A 48 48 0 0 0 50 2" />
            <path d="M 98 50 A 24 24 0 0 0 74 26" />
            <path d="M 98 50 A 12 12 0 0 0 86 38" />
            <path d="M 98 50 A 48 48 0 0 1 50 98" />
            <path d="M 98 50 A 24 24 0 0 1 74 74" />
            <path d="M 98 50 A 12 12 0 0 1 86 62" />
          </SmithChartIcon>
        )}
        {/* Recommendation Badge - only for generic amplifier components */}
        {!data.isCatalog && componentDef?.category === 'amplifiers' && catalogComponents.length > 0 && (
          <RecommendationBadge
            nodeId={id}
            nodeType={type}
            onReplace={(catalogComponent) => replaceNodeWithCatalog(id, catalogComponent)}
          />
        )}
      </Header>

      <Body style={{ minHeight: `${minBodyHeight}px` }}>
        {/* Component Symbol Watermark */}
        {COMPONENT_SYMBOLS[symbolType] && (
          <SymbolWatermark
            viewBox="0 0 40 40"
            xmlns="http://www.w3.org/2000/svg"
            $isBlue={type === 'inputSource' || type === 'noiseSource'}
          >
            <path d={COMPONENT_SYMBOLS[symbolType]} />
          </SymbolWatermark>
        )}

        {/* Voltage Selector - only for catalog components with frequency data */}
        {data.isCatalog && data.parameters?.hasFreqData?.value && (
          <VoltageSelector node={{ id, data }} />
        )}

        {displayParams.map(([key, param]) => {
          // Determine color for P1dB based on compression state
          let valueColor = undefined;
          if (key === 'p1db' && results && results.compressionState) {
            if (results.compressionState === 'saturated') valueColor = '#FF0000'; // Red
            else if (results.compressionState === 'compressed') valueColor = '#FF0000'; // Red
            else if (results.compressionState === 'warning') valueColor = '#FFA500'; // Orange
          }

          // Check if parameter supports data sources
          const dataSourceParams = ['gain', 'noiseFigure', 'p1db', 'oip3'];
          const supportsDataSource = dataSourceParams.includes(key) && data.isCatalog;
          const currentSource = supportsDataSource ? getCurrentDataSource({ id, data }, key) : 'manual';
          const availableSources = supportsDataSource ? getAvailableDataSources({ id, data, type }, key, componentLibrary) : [];
          const isManual = currentSource === 'manual';

          const handleContextMenu = (e) => {
            if (supportsDataSource && availableSources.length > 1) {
              e.preventDefault();
              e.stopPropagation(); // Prevent component-level context menu
              setContextMenu({
                x: e.clientX,
                y: e.clientY,
                paramKey: key,
                availableSources
              });
            }
          };

          return (
            <ParamRow key={key}>
              <ParamLabel>{param.name === 'P1dB' ? 'OP1dB' : param.name}:</ParamLabel>
              <ParamValueWrapper>
                {editingParam === key ? (
                  <ParamEditInput
                    className="nodrag"
                    type="number"
                    value={editValue}
                    onChange={handleEditChange}
                    onBlur={() => handleEditSave(key)}
                    onKeyDown={(e) => handleEditKeyDown(e, key)}
                    onWheelCapture={(e) => handleWheelAdjust(e, key)}
                    onFocus={(e) => e.target.select()}
                    autoFocus
                    step="any"
                  />
                ) : (
                  <>
                    {supportsDataSource && (
                      <DataSourceBadge
                        currentSource={currentSource}
                        availableSources={availableSources}
                        onContextMenu={handleContextMenu}
                        onSourceChange={(newSource) => handleDataSourceChange(id, key, newSource)}
                      />
                    )}
                    <EditableParamValue
                      className="nodrag"
                      onClick={isManual ? () => handleParamClick(key, param.value) : undefined}
                      onContextMenu={handleContextMenu}
                      $showUnderline={isManual}
                      $isEditable={isManual}
                      style={valueColor ? { color: valueColor } : undefined}
                    >
                      {(() => {
                        // Only show "(sweep)" for frequency-dependent data source parameters, not configuration params
                        const freqDependentParams = ['gain', 'noiseFigure', 'p1db', 'oip3'];
                        const isFreqDependent = freqDependentParams.includes(key) && currentSource !== 'manual';
                        if (frequencyMode === 'sweep' && type !== 'inputSource' && isFreqDependent) {
                          return <>(sweep)</>;
                        }
                        const displayData = getDisplayValue(key, param);
                        return (
                          <>
                            {displayData.value} {param.unit}
                            {displayData.warning && (
                              <OutOfRangeWarning title={`Frequency ${displayData.warning} S2P data range`}>
                                ⚠️
                              </OutOfRangeWarning>
                            )}
                          </>
                        );
                      })()}
                    </EditableParamValue>
                  </>
                )}
              </ParamValueWrapper>
            </ParamRow>
          );
        })}

        {/* Frequency Controls - Only for inputSource */}
        {type === 'inputSource' && (
          <FrequencyControlsSection>
            <FrequencyDivider />

            {/* Frequency Row - styled like other editable params */}
            <ParamRow>
              <ParamLabel>Freq:</ParamLabel>
              <ParamValueWrapper>
                <SweepToggleWrapper id="sweep-mode-toggle">
                  <SweepTooltip className="sweep-tooltip">
                    {frequencyMode === 'sweep' ? 'Switch to single frequency' : 'Switch to frequency sweep'}
                  </SweepTooltip>
                  <SweepToggleEmoji
                    className="nodrag"
                    onClick={() => {
                      const newMode = frequencyMode === 'sweep' ? 'single' : 'sweep';
                      setFrequencyMode(newMode);
                      if (newMode === 'sweep') {
                        api.track('event', 'sweep_enabled');
                      }
                      // Track enabling sweep mode for tutorial (step 17)
                      if (showTutorial && tutorialStep === 17 && newMode === 'sweep') {
                        completeTutorialAction('sweepModeEnabled');
                      }
                    }}
                    $active={frequencyMode === 'sweep'}
                  >
                    🧹
                  </SweepToggleEmoji>
                </SweepToggleWrapper>
                {frequencyMode === 'sweep' ? (
                  <FrequencyValueDisplay>(sweep)</FrequencyValueDisplay>
                ) : (
                  <EditableParamValue
                    className="nodrag"
                    onClick={() => handleParamClick('_frequency', frequencySingle)}
                    $showUnderline={true}
                    $isEditable={true}
                  >
                    {editingParam === '_frequency' ? (
                      <ParamEditInput
                        className="nodrag"
                        type="number"
                        value={editValue}
                        onChange={handleEditChange}
                        onBlur={() => {
                          const numValue = parseFloat(editValue);
                          if (!isNaN(numValue) && numValue > 0) {
                            setFrequencySingle(numValue);
                          }
                          setEditingParam(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const numValue = parseFloat(editValue);
                            if (!isNaN(numValue) && numValue > 0) {
                              setFrequencySingle(numValue);
                            }
                            setEditingParam(null);
                          } else if (e.key === 'Escape') {
                            setEditingParam(null);
                          }
                        }}
                        onFocus={(e) => e.target.select()}
                        autoFocus
                        step="any"
                      />
                    ) : (
                      <>{frequencySingle} MHz</>
                    )}
                  </EditableParamValue>
                )}
              </ParamValueWrapper>
            </ParamRow>

            {/* Sweep Parameters - shown when sweep enabled */}
            {frequencyMode === 'sweep' && (
              <SweepParamsContainer>
                <ParamRow>
                  <ParamLabel>Start:</ParamLabel>
                  <ParamValueWrapper>
                    {editingParam === '_freqStart' ? (
                      <ParamEditInput
                        className="nodrag"
                        type="number"
                        value={editValue}
                        onChange={handleEditChange}
                        onBlur={() => {
                          const numValue = parseFloat(editValue);
                          if (!isNaN(numValue) && numValue > 0) {
                            setFrequencySweepParams({ start: numValue });
                          }
                          setEditingParam(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const numValue = parseFloat(editValue);
                            if (!isNaN(numValue) && numValue > 0) {
                              setFrequencySweepParams({ start: numValue });
                            }
                            setEditingParam(null);
                          } else if (e.key === 'Escape') {
                            setEditingParam(null);
                          }
                        }}
                        onFocus={(e) => e.target.select()}
                        autoFocus
                        step="any"
                      />
                    ) : (
                      <EditableParamValue
                        className="nodrag"
                        onClick={() => handleParamClick('_freqStart', frequencyStart)}
                        $showUnderline={true}
                        $isEditable={true}
                      >
                        {frequencyStart} MHz
                      </EditableParamValue>
                    )}
                  </ParamValueWrapper>
                </ParamRow>
                <ParamRow>
                  <ParamLabel>Stop:</ParamLabel>
                  <ParamValueWrapper>
                    {editingParam === '_freqStop' ? (
                      <ParamEditInput
                        className="nodrag"
                        type="number"
                        value={editValue}
                        onChange={handleEditChange}
                        onBlur={() => {
                          const numValue = parseFloat(editValue);
                          if (!isNaN(numValue) && numValue > 0) {
                            setFrequencySweepParams({ stop: numValue });
                          }
                          setEditingParam(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const numValue = parseFloat(editValue);
                            if (!isNaN(numValue) && numValue > 0) {
                              setFrequencySweepParams({ stop: numValue });
                            }
                            setEditingParam(null);
                          } else if (e.key === 'Escape') {
                            setEditingParam(null);
                          }
                        }}
                        onFocus={(e) => e.target.select()}
                        autoFocus
                        step="any"
                      />
                    ) : (
                      <EditableParamValue
                        className="nodrag"
                        onClick={() => handleParamClick('_freqStop', frequencyStop)}
                        $showUnderline={true}
                        $isEditable={true}
                      >
                        {frequencyStop} MHz
                      </EditableParamValue>
                    )}
                  </ParamValueWrapper>
                </ParamRow>
                <ParamRow>
                  <ParamLabel>Step:</ParamLabel>
                  <ParamValueWrapper>
                    {editingParam === '_freqStep' ? (
                      <ParamEditInput
                        className="nodrag"
                        type="number"
                        value={editValue}
                        onChange={handleEditChange}
                        onBlur={() => {
                          const numValue = parseFloat(editValue);
                          if (!isNaN(numValue) && numValue > 0) {
                            setFrequencySweepParams({ step: numValue });
                          }
                          setEditingParam(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const numValue = parseFloat(editValue);
                            if (!isNaN(numValue) && numValue > 0) {
                              setFrequencySweepParams({ step: numValue });
                            }
                            setEditingParam(null);
                          } else if (e.key === 'Escape') {
                            setEditingParam(null);
                          }
                        }}
                        onFocus={(e) => e.target.select()}
                        autoFocus
                        step="any"
                      />
                    ) : (
                      <EditableParamValue
                        className="nodrag"
                        onClick={() => handleParamClick('_freqStep', frequencyStep)}
                        $showUnderline={true}
                        $isEditable={true}
                      >
                        {frequencyStep} MHz
                      </EditableParamValue>
                    )}
                  </ParamValueWrapper>
                </ParamRow>
                {isSweeping && (
                  <SweepStatus>⏳ Calculating...</SweepStatus>
                )}
              </SweepParamsContainer>
            )}
          </FrequencyControlsSection>
        )}

        {results && results.outputPower && type !== 'inputSource' && (
          <ResultRow>
            <ResultLabel>Pout:</ResultLabel>
            <ResultValue
              style={
                results.compressionState === 'saturated' ? { color: '#FF0000' } :
                results.compressionState === 'compressed' ? { color: '#FF0000' } :
                results.compressionState === 'warning' ? { color: '#FFA500' } :
                undefined
              }
            >
              {frequencyMode === 'sweep' ? '(sweep)' :
                results.compressionState === 'saturated' ? `(sat) ${results.outputPower} dBm` : `${results.outputPower} dBm`}
            </ResultValue>
          </ResultRow>
        )}

        {/* Show Gain for non-amplifier components, or components without a gain parameter */}
        {results && results.gain !== undefined && results.gain !== '0.00' &&
         componentDef?.category !== 'amplifiers' && (
          <ResultRow>
            <ResultLabel>Gain:</ResultLabel>
            <ResultValue>
              {frequencyMode === 'sweep' ? '(sweep)' : `${results.gain} dB`}
            </ResultValue>
          </ResultRow>
        )}

        {/* Show Cascaded Gain (renamed from System Gain), but not for first gain component */}
        {results && results.cumulativeGain !== undefined && results.cumulativeGain !== '0.00' &&
         !results.isFirstGainComponent && (
          <ResultRow>
            <ResultLabel>Cascaded Gain:</ResultLabel>
            <ResultValue>
              {frequencyMode === 'sweep' ? '(sweep)' : `${results.cumulativeGain} dB`}
            </ResultValue>
          </ResultRow>
        )}

      </Body>

      {/* Smith Chart - Modal or Floating Window based on toggle */}
      {showSmithChart && (
        USE_MODAL_SMITH_CHART ? (
          <SmithChartModal
            isOpen={showSmithChart}
            onClose={() => setShowSmithChart(false)}
            componentId={componentLibrary[type]?.id}
            componentName={data.alias || data.partNumber || data.name || componentDef?.displayName || type}
          />
        ) : (
          <SmithChartWindow
            isOpen={showSmithChart}
            onClose={() => setShowSmithChart(false)}
            componentId={componentLibrary[type]?.id}
            componentName={data.alias || data.partNumber || data.name || componentDef?.displayName || type}
          />
        )
      )}

      {/* Output Handles */}
      {hasOutputs && (
        <>
          {componentDef.outputs.map((output, idx) => {
            const numOutputs = componentDef.outputs.length;
            const spacing = numOutputs > 1 ? 40 : 0;
            const startOffset = 60;
            return (
              <Handle
                key={output}
                type="source"
                position={Position.Right}
                id={output}
                style={{
                  top: `${startOffset + idx * spacing}px`,
                  background: THEME.handleColor,
                  border: `2px solid ${THEME.textPrimary}`
                }}
              />
            );
          })}
        </>
      )}

      {/* Context Menu for Data Source Selection */}
      {contextMenu && (
        <>
          <ContextMenuBackdrop onClick={() => setContextMenu(null)} />
          <ContextMenu style={{ left: contextMenu.x, top: contextMenu.y }}>
            {contextMenu.availableSources.map(source => (
              <ContextMenuItem
                key={source.value}
                onClick={async () => {
                  try {
                    await handleDataSourceChange(id, contextMenu.paramKey, source.value);
                    setContextMenu(null);
                  } catch (error) {
                    console.error('Failed to change data source:', error);
                  }
                }}
              >
                {source.label}
              </ContextMenuItem>
            ))}
          </ContextMenu>
        </>
      )}
    </NodeContainer>
  );
};

const NodeContainer = styled.div`
  min-width: 180px;
  max-width: 220px;
  background: ${THEME.cardBackground};
  border: 2px solid ${THEME.cardBorder};
  border-radius: ${THEME.borderRadiusMedium};
  box-shadow: ${THEME.shadowMedium};
  transition: all ${THEME.transitionFast};

  &:hover {
    box-shadow: ${THEME.shadowLarge};
  }

  .react-flow__node.selected & {
    border-color: ${THEME.accentPrimary};
    box-shadow: 0 0 0 2px ${THEME.accentPrimary};
  }
`;

const Header = styled.div`
  position: relative;
  padding: ${THEME.spacing.xs} ${THEME.spacing.md};
  border-radius: ${THEME.borderRadiusMedium} ${THEME.borderRadiusMedium} 0 0;
  text-align: center;
  transition: background-color ${THEME.transitionMedium};
  display: flex;
  flex-direction: column;
  gap: 2px;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

const SmithChartIcon = styled.svg`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity ${THEME.transitionFast}, transform ${THEME.transitionFast};

  &:hover {
    opacity: 1;
    transform: translateY(-50%) scale(1.15);
  }

  circle, path, line {
    fill: none;
    stroke: ${THEME.accentPrimary};
    stroke-width: 2;
  }
`;

const HeaderText = styled.div`
  font-size: ${THEME.fontSize.xs};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ComponentTypeText = styled.div`
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  line-height: 1.2;
  color: #000;
  opacity: 0.85;
`;

const PartNumberText = styled.div`
  font-size: ${THEME.fontSize.sm};
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.2;
  color: #ff6b6b;
`;

const AliasSubtext = styled.div`
  font-size: 9px;
  font-weight: 400;
  color: ${THEME.textSecondary};
  opacity: 0.7;
  font-style: italic;
  line-height: 1.2;
  margin-top: -2px;
`;

const AliasInput = styled.input`
  width: 100%;
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(59, 130, 246, 0.5);
  padding: 0;
  font-size: ${THEME.fontSize.sm};
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${props => props.$textColor || '#000'};
  outline: none;
  text-align: center;

  &::placeholder {
    color: ${props => props.$textColor ? `${props.$textColor}60` : 'rgba(0, 0, 0, 0.4)'};
    font-style: italic;
  }

  &:focus {
    border-bottom: 1px solid rgba(59, 130, 246, 0.8);
  }
`;

const Body = styled.div`
  padding: ${THEME.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${THEME.spacing.xs};
  position: relative;
  min-height: 100px;
  cursor: default;
`;

const SymbolWatermark = styled.svg`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 160px;
  height: 160px;
  opacity: 0.15;
  pointer-events: none;
  z-index: 0;

  path {
    fill: none;
    stroke: ${props => props.$isBlue ? '#3b82f6' : '#ff6b6b'};
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const ParamRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 1;
`;

const ParamLabel = styled.span`
  color: ${THEME.textSecondary};
  font-size: ${THEME.fontSize.xs};
`;

const ParamValueWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ParamValue = styled.span`
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.xs};
  font-weight: 600;
`;

const EditableParamValue = styled.span`
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.xs};
  font-weight: 600;
  font-style: italic;
  text-decoration: ${props => props.$showUnderline ? 'underline' : 'none'};
  padding: 2px 4px;
  border-radius: 2px;
  cursor: ${props => props.$isEditable ? 'pointer' : 'default'};
  transition: all ${THEME.transitionFast};

  &:hover {
    background: ${props => props.$isEditable ? 'rgba(220, 20, 60, 0.08)' : 'transparent'};
  }
`;

const ParamEditInput = styled.input`
  background: ${THEME.inputBackground};
  color: ${THEME.textPrimary};
  border: 1px solid ${THEME.inputBorderFocus};
  border-radius: 3px;
  padding: 2px 6px;
  font-size: ${THEME.fontSize.xs};
  font-weight: 600;
  width: 60px;
  text-align: center;
  box-shadow: 0 0 0 1px ${THEME.inputBorderFocus};

  &:focus {
    outline: none;
  }

  /* Remove number input spinners */
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

const ResultRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${THEME.spacing.xs};
  border-top: 1px solid #404040;
  margin-top: ${THEME.spacing.xs};
  position: relative;
  z-index: 1;
`;

const ResultLabel = styled.span`
  color: ${THEME.textSecondary};
  font-size: ${THEME.fontSize.xs};
  font-weight: 600;
`;

const ResultValue = styled.span`
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.xs};
  font-weight: 700;
  font-style: italic;
  padding-right: 3px;
`;

const ContextMenuBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9998;
`;

const ContextMenu = styled.div`
  position: fixed;
  background: #2a2a2a;
  border: 1px solid #444;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  z-index: 9999;
  min-width: 120px;
  padding: 4px 0;
`;

const ContextMenuItem = styled.div`
  padding: 8px 16px;
  color: #e0e0e0;
  cursor: pointer;
  font-size: 13px;
  transition: background 150ms ease;

  &:hover {
    background: rgba(220, 20, 60, 0.2);
  }

  &:active {
    background: rgba(220, 20, 60, 0.3);
  }
`;

const OutOfRangeWarning = styled.span`
  margin-left: 4px;
  font-size: 11px;
  color: #f59e0b;
  cursor: help;
  animation: pulse 2s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.6;
    }
  }
`;

// Frequency Controls Styled Components
const FrequencyControlsSection = styled.div`
  position: relative;
  z-index: 1;
  margin-top: ${THEME.spacing.xs};
`;

const FrequencyDivider = styled.div`
  height: 1px;
  background: #404040;
  margin-bottom: ${THEME.spacing.sm};
`;

const SweepToggleWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin-right: -4px;

  &:hover .sweep-tooltip {
    opacity: 1;
    visibility: visible;
  }
`;

const SweepToggleEmoji = styled.span`
  font-size: 16px;
  cursor: pointer;
  opacity: ${props => props.$active ? 1 : 0.4};
  filter: ${props => props.$active ? 'none' : 'grayscale(80%)'};
  transition: all 0.15s ease;

  &:hover {
    opacity: 1;
    filter: none;
    transform: scale(1.15);
  }
`;

const SweepTooltip = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #2a2a2a;
  border: 1px solid #444;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 10px;
  color: #e0e0e0;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease, visibility 0.15s ease;
  z-index: 100;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  margin-bottom: 4px;
`;

const FrequencyValueDisplay = styled.span`
  color: ${THEME.textSecondary};
  font-size: ${THEME.fontSize.xs};
  font-weight: 600;
  font-style: italic;
`;

const SweepParamsContainer = styled.div`
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px dashed ${THEME.inputBorder};
`;

const SweepStatus = styled.div`
  text-align: center;
  font-size: 10px;
  color: ${THEME.textSecondary};
  padding: 4px;
  margin-top: ${THEME.spacing.xs};
`;

export default memo(RFNode);
